import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { PolicyQueryService } from './policy-query.service';
import { ToastrService } from 'ngx-toastr';

@Injectable({
  providedIn: 'root'
})
export class AdminUserService {
  private usersSource = new BehaviorSubject<ApplicationUserApi[]>(null);
  users = this.usersSource.asObservable();
  private selectedUserSource = new BehaviorSubject<ApplicationUserApi>(null);
  selectedUser = this.selectedUserSource.asObservable();
  private currentUserSource = new BehaviorSubject<ApplicationUserApi>(null);
  currentUser = this.currentUserSource.asObservable();

  private userRolesSource = new BehaviorSubject<any>(null);
  roles = this.userRolesSource.asObservable();

  private userDNRSource = new BehaviorSubject<any>(null);
  isDNR = this.userDNRSource.asObservable();

  private userAdminSource = new BehaviorSubject<ClaimApi>(null);
  isAdmin = this.userAdminSource.asObservable();

  constructor(
    private http: HttpClient, 
    @Inject('BASE_URL') private baseUrl: string, 
    private policyQueryService: PolicyQueryService,
    private toastr: ToastrService) {
  }

  async isadmin() {
    this.http.get<ClaimApi>(`${this.baseUrl}api/identity/isadmin`, {observe: 'response'}).subscribe(
      response => {
        if(response.status === 200) {
          //console.log('admin service: isadmin: ' + response.body);
          this.changeIsAdmin(response.body);
        }
      },
      error => {
        //console.log(error)
        let isuseradmin : ClaimApi = { claimType: "IsAdmin", claimValue: "no" }
        this.changeIsAdmin(isuseradmin);
      });
  }

  async checkIsDNR() {
    this.http.get<ClaimApi>(`${this.baseUrl}api/identity/isDNR`, { observe: 'response' }).subscribe(
      response => {
        if (response.status === 200) {
          this.changeIsDNR(response.body);
        }
      },
      error => {
        console.warn(error);
        let isUserDNR: ClaimApi = { claimType: "IsDNR", claimValue: "no" };
        this.changeIsDNR(isUserDNR);
      }
    );
  }

  async getusers() {
    this.http.get<ApplicationUserApi[]>(`${this.baseUrl}api/identity/getallusers`, {observe: 'response'}).subscribe(
      response => {
        if(response.status === 200) {
          this.usersSource.next(response.body);
        }
      },
      error => console.log(error)
      );
  }

  async getroles() {
    this.http.get(`${this.baseUrl}api/identity/listroles`, {observe: 'response'}).subscribe(
      response => {
        if(response.status === 200) {
          this.userRolesSource.next(response.body);
        }
      },
      error => console.log(error)
      );
  }
   
  async login(username: string, password: string) {
    var user = {
      username: username,
      password: password 
    }
    this.http.post(`${this.baseUrl}api/identity/login`, user, {observe: 'response'}).subscribe(
      response => {
        if(response.status === 200) {
          //console.log('Token: ' + response.body);
        }
      },
      error => console.log(error)
    );
    return { success: true, failureMessage: null }; // as LoginResult;
  }

  async register(username: string, email: string, password: string, authenticated: boolean) {
    var user = {
      username: username,
      email: email,
      password: password
    }
    this.http.post(`${this.baseUrl}api/identity/register`, user, {observe: 'response'}).subscribe(
      response => {
        if(response.status === 200) {
          //console.log(JSON.stringify(response));
          if (authenticated) {
            this.getusers();
          }
        }
      },
      error => {
        //console.log(error); 
        return { success: false, failureMessage: error }; //as LoginResult;
      }
    );
    return { success: true, failureMessage: null }; //as LoginResult;
  }

  async createrole(rolename: string) {
    var newrole = {
      rolename: rolename,
    }
    this.http.post(`${this.baseUrl}api/identity/createrole`, newrole, {observe: 'response'}).subscribe(
      response => {
        if(response.status === 200) {
          this.getroles();
        }
      },
      error => {
        //console.log(error); 
        return { success: false, failureMessage: error }; //as LoginResult;
      }
    );
    return { success: true, failureMessage: null }; //as LoginResult;
  }

  async updateuser(user: ApplicationUserApi) {
    this.http.post(`${this.baseUrl}api/identity/updateuserclaims`, user, {observe: 'response'}).subscribe(
      response => {
        if(response.status === 200) {
          this.getusers();
          this.toastr.success('User information is updated successfully.', 'Update User');
        }
      },
      error => {
        //console.log(error); 
        return { success: false, failureMessage: error }; //as LoginResult;
      }
    );
    return { success: true, failureMessage: null }; //as LoginResult;
  }

  updatePassword(data) {
    this.http.post(`${this.baseUrl}api/identity/changepassword`, data, {observe: 'response'}).subscribe(
      response => {
        if(response.status === 200) {
          this.toastr.success('User password is changed successfully.', 'Change password');
        }
      },
      errorResponse => {
        var errors = errorResponse.error.errors;
        return { success: false, failureMessage: errorResponse.message }; //as LoginResult;
      }
    );
    return { success: true, failureMessage: null }; //as LoginResult;

  }

  getCurrentUser() {
    this.http.get<ApplicationUserApi>(`${this.baseUrl}api/identity/currentuser`, { observe: 'response' }).subscribe(
      response => {
        if (response.status === 200) {
          this.changeCurrentUser(response.body);
        }
      },
      error => {
        console.log(error);
      }
    );
  }

  updateCurrentUser(data) {
    this.http.put<ApplicationUserApi>(`${this.baseUrl}api/identity/currentuser`, data, { observe: 'response' }).subscribe(
      response => {
        if (response.status === 200) {
          this.changeCurrentUser(response.body);
          this.toastr.success('User email changed successfully.', 'Change email');
        }
      },
      error => {
        console.log(error);
      }
    )
  }

  changeIsAdmin(isadmin: ClaimApi) {
    this.userAdminSource.next(isadmin);
  }

  changeIsDNR(isDNR: ClaimApi) {
    this.userDNRSource.next(isDNR);
  }

  changeSelectedUser(user: ApplicationUserApi) {
    this.selectedUserSource.next(user);
  }

  changeCurrentUser(user: ApplicationUserApi) {
    this.currentUserSource.next(user);
  }
}

export interface ApplicationUserApi {
  id: number,
  name: string,
  email: string,
  password: string,
  newPassword: string,
  dssClaims: ClaimApi[],
  roles: ClaimApi[],
}

export interface ClaimApi {
  claimType: string,
  claimValue: string
}
